import {LogLevels} from '@pexip/logger';

import type {FrontendRecordSource} from './record';

/** The console methods a host captures; `console.log` is reported as `info`. */
export type ConsoleLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * What a host's capture reports: console calls and the window's uncaught
 * errors and promise rejections. Every value other than `kind`, `level`,
 * `time` and `count` is a bounded, JSON-safe copy taken when it happened, not
 * yet redacted.
 */
export type CapturedEvent =
    | {
          readonly kind: 'console';
          readonly level: ConsoleLevel;
          /** Epoch milliseconds. */
          readonly time: number;
          readonly args: readonly unknown[];
      }
    | {
          /** An uncaught error, from the window's `error` event. */
          readonly kind: 'window-error';
          readonly time: number;
          readonly message: unknown;
          readonly location: {
              readonly source?: unknown;
              readonly line?: unknown;
              readonly column?: unknown;
          };
          readonly error: unknown;
      }
    | {
          /** A promise rejection nothing handled. */
          readonly kind: 'unhandled-rejection';
          readonly time: number;
          readonly reason: unknown;
      }
    | {
          /** A console call in a dedicated worker. */
          readonly kind: 'worker-console';
          /** The registered worker name, used as the record target. */
          readonly worker: string;
          readonly level: ConsoleLevel;
          readonly time: number;
          readonly args: readonly unknown[];
      }
    | {
          /** An uncaught error in a dedicated worker. */
          readonly kind: 'worker-error';
          readonly worker: string;
          readonly time: number;
          readonly message: unknown;
          readonly location: {
              readonly source?: unknown;
              readonly line?: unknown;
              readonly column?: unknown;
          };
          readonly error: unknown;
      }
    | {
          /** A promise rejection nothing handled in a dedicated worker. */
          readonly kind: 'worker-unhandled-rejection';
          readonly worker: string;
          readonly time: number;
          readonly reason: unknown;
      }
    | {
          /** Events the host could not keep, counted, never described. */
          readonly kind: 'dropped';
          readonly count: number;
          readonly time: number;
      };

/** A host's console capture, as the application uses it. */
export interface ConsoleCapture {
    /**
     * Delivers every event captured so far, then each later one. The
     * application attaches once, when its logger can redact events.
     */
    attach(listener: (event: CapturedEvent) => void): void;
    /**
     * Runs `output` without capturing the console calls it makes, so the
     * logger's own console output is not recorded twice.
     */
    withoutCapture(output: () => void): void;
}

const consoleLevels = {
    debug: LogLevels.debug,
    info: LogLevels.info,
    warn: LogLevels.warn,
    error: LogLevels.error,
} as const;

/**
 * The logger level of a captured event: its console method's level, `error`
 * for uncaught errors and rejections, and `warn` for dropped events.
 *
 * @example
 * capturedLevel({kind: 'console', level: 'debug', time: 1, args: []});
 * // 20
 */
export const capturedLevel = (event: CapturedEvent): LogLevels => {
    switch (event.kind) {
        case 'console':
        case 'worker-console':
            return consoleLevels[event.level];
        case 'window-error':
        case 'worker-error':
        case 'unhandled-rejection':
        case 'worker-unhandled-rejection':
            return LogLevels.error;
        case 'dropped':
            return LogLevels.warn;
        default: {
            const unreachable: never = event;
            return unreachable;
        }
    }
};

/**
 * Turns a captured event into a logger-shaped event, so it can be redacted
 * exactly like a logger event. A leading string argument becomes the message.
 *
 * @example
 * toCapturedLogEvent({kind: 'console', level: 'info', time: 1, args: ['Ready', 2]});
 * // {level: 30, time: 1, name: 'console', msg: 'Ready', arguments: [2]}
 */
export const toCapturedLogEvent = (
    event: CapturedEvent,
): Record<string, unknown> => {
    switch (event.kind) {
        case 'console':
        case 'worker-console': {
            const [first, ...rest] = event.args;
            const [msg, args] =
                typeof first === 'string' ? [first, rest] : ['', event.args];
            return {
                level: capturedLevel(event),
                time: event.time,
                name: event.kind === 'console' ? 'console' : event.worker,
                msg,
                ...(args.length > 0 ? {arguments: args} : {}),
            };
        }
        case 'window-error':
        case 'worker-error':
            return {
                level: capturedLevel(event),
                time: event.time,
                name: event.kind === 'window-error' ? 'window' : event.worker,
                msg:
                    typeof event.message === 'string'
                        ? event.message
                        : 'Uncaught error',
                location: event.location,
                error: event.error,
            };
        case 'unhandled-rejection':
        case 'worker-unhandled-rejection':
            return {
                level: capturedLevel(event),
                time: event.time,
                name:
                    event.kind === 'unhandled-rejection'
                        ? 'window'
                        : event.worker,
                msg: 'Unhandled promise rejection',
                reason: event.reason,
            };
        case 'dropped':
            return {
                level: capturedLevel(event),
                time: event.time,
                name: 'console',
                msg: 'Captured records were dropped',
                count: event.count,
            };
        default: {
            const unreachable: never = event;
            return unreachable;
        }
    }
};

/**
 * The record source for a captured event.
 *
 * @example
 * capturedSource({kind: 'unhandled-rejection', time: 1, reason: null});
 * // 'unhandled-rejection'
 */
export const capturedSource = (event: CapturedEvent): FrontendRecordSource => {
    switch (event.kind) {
        case 'console':
        case 'dropped':
            return 'console';
        case 'window-error':
            return 'window-error';
        case 'unhandled-rejection':
            return 'unhandled-rejection';
        case 'worker-console':
            return 'worker-console';
        case 'worker-error':
            return 'worker-error';
        case 'worker-unhandled-rejection':
            return 'worker-unhandled-rejection';
        default: {
            const unreachable: never = event;
            return unreachable;
        }
    }
};
